<template>
    <BaseForm @submit="submitCode">
        <template
            v-if="$slots.title"
            #title
        >
            <slot name="title" />
        </template>

        <template
            v-else
            #title
        >
            Check je inbox
        </template>

        <template
            v-if="$slots.before"
            #before
        >
            <slot name="before" />
        </template>

        <template
            v-else
            #before
        >
            We hebben je een e-mail gestuurd met een verificatiecode.
            Vul de code hieronder in om je e-mailadres te verifiëren.
            Indien je geen actief account hebt, ontvang je geen e-mail.
        </template>

        <template #content>
            <FieldValidator
                v-slot="{ field, meta, errors }"
                name="code"
            >
                <BaseFormField
                    :errors="errors"
                >
                    <template #label>
                        Code
                    </template>

                    <BaseInput
                        v-bind="field"
                        type="text"
                        :validator="meta"
                    />
                </BaseFormField>
            </FieldValidator>

            <div
                v-if="!!errorMessage"
                class="form__error"
            >
                {{ errorMessage }}
            </div>
        </template>

        <template #actions>
            <BaseButton
                type="submit"
                class="form__submit button--secondary form__button--max-width"
            >
                {{ confirmText }}
            </BaseButton>
        </template>

        <template #after>
            Het kan een paar minuten duren voordat je de code ontvangen hebt.
            <br><br>
            Geen code ontvangen? Check je spam of
            <BaseButton
                type="button"
                class="button button--link"
                @click.prevent="emit('backToStart')"
            >
                probeer nogmaals
            </BaseButton>.
        </template>
    </BaseForm>
</template>

<script setup lang="ts">
import {
    STATUS,
    PASSWORD_RESET_INVALID_CODE,
    PASSWORD_RESET_TOO_MANY_ATTEMPTS,
} from '~/constants/errorMessages';
import { useForm, Field as FieldValidator } from 'vee-validate';

const emit = defineEmits(['nextStep', 'backToStart']);

withDefaults(
    defineProps<{
        confirmText?: string;
    }>(),
    {
        confirmText:  'Volgende stap'
    }
);

const errorMessage = ref<string|null>(null);

const signUpStore = useSignUpStore();

const { email } = storeToRefs(signUpStore);

const { handleSubmit } = useForm<{code: string}>({
    validationSchema: {
        code: 'required'
    }
});

const submitCode = handleSubmit(async(formData) => {
    errorMessage.value = null;

    const { data, error } = await useFetchLaravel<{
        user?: User,
        token?: string
    }>('/v2.0/user/email-confirmation/check', {
        method: 'POST',
        data: {
            email: email.value,
            token: formData.code
        }
    });

    if (error.value?.status === 429) {
        errorMessage.value = PASSWORD_RESET_TOO_MANY_ATTEMPTS;
        return;
    } else if (error.value?.status === 400) {
        errorMessage.value = PASSWORD_RESET_INVALID_CODE;
        return;
    } else if (error.value) {
        errorMessage.value = STATUS['500'];
        return;
    }

    if (data.value?.user) {
        signUpStore.setUser(data.value.user);
    }

    if (data.value?.token) {
        signUpStore.setToken(data.value.token);
    } else {
        errorMessage.value = STATUS['500'];
        return;
    }

    emit('nextStep');
});
</script>
